import { Device, TwilioError } from '@twilio/voice-sdk';
import { ProviderCategoryEnum, useCreateProviderErrorLogMutation } from '@/types/generated/graphql';
import { TWILIO_DEVICE_EVENTS } from '@shared/constants';
import { useEffect } from 'react';
import { debounce, fibonacciSequence } from '@shared/utils';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { TwilioCall } from '@/types/app.types';
import { useProviderLogin } from './use-provider-login';
import { isJwtExpiringSoon } from '@shared/utils/jwt.helper';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { normalizeTwilioIncomingCall } from './helpers';
import { AppIncomingCall } from '../types';

const ACCESS_TOKEN_ERROR_NAME = 'AccessTokenExpired';

export function useDeviceEvents(onIncomingCall: (call: AppIncomingCall) => void) {
  const { t } = useTranslation();
  const [createProviderError] = useCreateProviderErrorLogMutation();
  const [...retrySequence] = fibonacciSequence();
  let currentFibonaaci = [...retrySequence];

  const { device: activeDevice } = useDeviceStore();
  const device = (activeDevice?.providerDevice as Device | undefined) ?? null;
  const { refetchProviderLogin } = useProviderLogin();

  function handleTokenWillExpire() {
    refetchProviderLogin();
  }

  function twilioIncomingCall(twilioIncomingCall: TwilioCall) {
    const incomingCallObject = normalizeTwilioIncomingCall(twilioIncomingCall, device);
    onIncomingCall(incomingCallObject);
  }

  function handleTwilioError(error: TwilioError.TwilioError) {
    const isAccessTokenError = error.name === ACCESS_TOKEN_ERROR_NAME;

    createProviderError({
      variables: {
        input: {
          error: {
            code: error.code,
            message: error.message,
            stackTrace: JSON.stringify(error),
          },
          category: isAccessTokenError ? ProviderCategoryEnum.auth : ProviderCategoryEnum.voice,
        },
      },
    });

    if (isAccessTokenError) {
      if (currentFibonaaci.length) {
        debounce(refetchProviderLogin, currentFibonaaci[0] * 1000)();
        currentFibonaaci.shift();
      } else {
        retrySequence.reverse();
        currentFibonaaci = retrySequence;
      }
      return;
    }

    // AccessTokenExpired retries silently above; every other device/registration error
    // is otherwise unrecoverable from here, so surface it instead of dropping it.
    notification.error({ message: t('call_feature_unavailable') });
  }

  function handleDeviceUnregistered() {
    // also fires during expected token-refresh re-registration cycles, so only log here;
    // user-facing notification is left to handleTwilioError, which fires for actual failures.
    console.error('Twilio device unregistered');
  }

  /**@description register and unregister device events */
  function handleDeviceEvents() {
    if (!device) return;

    device?.on(TWILIO_DEVICE_EVENTS.tokenWillExpire, handleTokenWillExpire);
    device?.on(TWILIO_DEVICE_EVENTS.incoming, twilioIncomingCall);
    device?.on(TWILIO_DEVICE_EVENTS.error, handleTwilioError);
    device?.on(TWILIO_DEVICE_EVENTS.unregistered, handleDeviceUnregistered);

    return handleUnregisterDeviceEvents;
  }

  function handleUnregisterDeviceEvents() {
    device?.off(TWILIO_DEVICE_EVENTS.tokenWillExpire, handleTokenWillExpire);
    device?.off(TWILIO_DEVICE_EVENTS.incoming, twilioIncomingCall);
    device?.off(TWILIO_DEVICE_EVENTS.error, handleTwilioError);
    device?.off(TWILIO_DEVICE_EVENTS.unregistered, handleDeviceUnregistered);
  }

  function validateDeviceTokenAndRefetch() {
    if (!device || document.hidden) return;
    if (isJwtExpiringSoon(device?.token || '')) refetchProviderLogin();
  }

  function handleVisibilityChange() {
    document.addEventListener('visibilitychange', validateDeviceTokenAndRefetch);
    return () => document.removeEventListener('visibilitychange', validateDeviceTokenAndRefetch);
  }

  useEffect(handleDeviceEvents, [device]);
  useEffect(handleVisibilityChange, [device]);
}
